import { Box, Button, Stack, Typography } from '@mui/material';
import { ResponsiveContainer } from 'components-new';
import { useState } from 'react';
import ReactPlayer from 'react-player';
import Slider from 'react-slick';
import { VolumeControls } from './VolumeControls';

type Props = {
  tour: any;
  isDesktop: boolean;
};

export function Header({ tour, isDesktop }: Props) {
  const { videoUrl } = tour;
  const [muted, setMuted] = useState(true);

  const handleClick = (id: string) => () => {
    const el = document.getElementById(id);

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  };

  return (
    <ResponsiveContainer
      ratio={isDesktop ? '16/9' : 'cover'}
      containerStyles={{
        borderRadius: '5px',
        overflow: 'hidden',
        marginTop: '70px',
        position: 'relative',
        '&:hover': {
          '& #volume-controls': {
            transition: 'all 0.3s ease-in-out',
            opacity: 1,
          },
        },
      }}
    >
      {videoUrl && <VolumeControls videoUrl={videoUrl} muted={muted} setMuted={setMuted} />}
      <Box
        position="relative"
        id="overview"
        width="100%"
        height="100%"
        sx={{
          '.slick-slider, .slick-list, .slick-track, .slick-slide, .slick-slide>div, .slick-slide>div>img':
            {
              height: '100%',
              objectFit: 'cover',
            },
        }}
      >
        {videoUrl ? (
          <Box width="100%" height="100%" sx={{ pointerEvents: 'none', position: 'relative' }}>
            <ReactPlayer
              width="100%"
              height="100%"
              controls={false}
              url={videoUrl}
              muted={muted}
              volume={1}
              playing
              loop
              config={{
                youtube: {
                  playerVars: {
                    disablekb: 1,
                    controls: 0,
                    showinfo: 0,
                    frameborder: 0,
                    title: 0,
                    modestbranding: 0,
                  },
                },
              }}
            />
          </Box>
        ) : (
          <Slider
            infinite
            arrows={false}
            autoplay
            dots={false}
            fade
            autoplaySpeed={5000}
            speed={2000}
            cssEase="cubic-bezier(0.7, 0, 0.3, 1)"
          >
            {tour?.images?.slice(1, 7).map((image: any, i: number) => (
              <Box
                key={i}
                component="img"
                src={`${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${image.url}`}
              />
            ))}
          </Slider>
        )}

        <Stack
          sx={{
            background: 'rgba(0,0,0,0.5)',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            color: '#fff',
            borderRadius: isDesktop ? '0 0 5px 5px' : '0',
            backdropFilter: 'blur(2px)',
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack spacing={1} justifyContent="center">
              <Typography variant="h3">{tour?.title}</Typography>
              {tour?.description && (
                <Typography
                  variant="h6"
                  fontWeight="lighter"
                  sx={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    overflowWrap: 'break-word',
                  }}
                >
                  {tour?.description}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" alignItems="center">
              <Box minWidth={220} textAlign={isDesktop ? 'right' : 'left'}>
                <Button
                  variant="contained"
                  color="primary"
                  size={isDesktop ? 'large' : 'medium'}
                  sx={{ color: '#fff', mr: 2 }}
                  onClick={handleClick('gallery')}
                >
                  {tour?.translation?.gallery || 'Gallery'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size={isDesktop ? 'large' : 'medium'}
                  sx={{ color: '#fff' }}
                  onClick={handleClick('3_d_tour')}
                >
                  {tour?.translation?.threeDTour || '3D Tour'}
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </ResponsiveContainer>
  );
}
